import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Modal,
  Row,
  Col,
  Image,
  Spinner,
} from "react-bootstrap";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import {
  createClientReview,
  getAllClientReviews,
  getClientReviewById,
  updateClientReview,
  deleteClientReview,
} from "api/apis";
import { toast } from "react-toastify";

interface ClientReview {
  _id: string;
  name: string;
  address: string;
  content: string;
  imageUrl: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const ClientReviews = () => {
  const [reviews, setReviews] = useState<ClientReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentReview, setCurrentReview] = useState<ClientReview | null>(null);
  const [formData, setFormData] = useState<FormData>(new FormData());
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Fetch all reviews on component mount
  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const data = await getAllClientReviews();
      setReviews(data);
    } catch (error) {
      toast.error("Failed to fetch reviews");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const newFormData = new FormData();

    // Copy existing form data
    // @ts-ignore - FormData doesn't support iteration in TypeScript by default
    for (let [key, val] of formData.entries()) {
      newFormData.append(key, val);
    }

    newFormData.set(name, value);
    setFormData(newFormData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const newFormData = new FormData();

      // Copy existing form data
      // @ts-ignore
      for (let [key, val] of formData.entries()) {
        newFormData.append(key, val);
      }

      newFormData.set("image", file);
      setFormData(newFormData);

      // Create preview URL
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    try {
      if (editMode && currentReview) {
        await updateClientReview(currentReview._id, formData);
        toast.success("Review updated successfully");
      } else {
        await createClientReview(formData);
        toast.success("Review created successfully");
      }
      fetchReviews();
      handleCloseModal();
    } catch (error) {
      toast.error(
        editMode ? "Failed to update review" : "Failed to create review"
      );
      console.error(error);
    }
  };

  const handleEdit = async (id: string) => {
    try {
      const review = await getClientReviewById(id);
      setCurrentReview(review);

      // Initialize form data with review values
      const newFormData = new FormData();
      newFormData.set("name", review.name);
      newFormData.set("address", review.address);
      newFormData.set("content", review.content);

      setFormData(newFormData);
      setPreviewImage(review.imageUrl || null);
      setEditMode(true);
      setShowModal(true);
    } catch (error) {
      toast.error("Failed to load review for editing");
      console.error(error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this review?")) {
      try {
        await deleteClientReview(id);
        setReviews((prev) => prev.filter((review) => review._id !== id));
        toast.success("Review deleted successfully");
      } catch (error) {
        toast.error("Failed to delete review");
        console.error(error);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setCurrentReview(null);
    setFormData(new FormData());
    setPreviewImage(null);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ marginTop: "8rem" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Client Reviews</h2>
        <Button variant="primary" onClick={() => setShowModal(true)}>
          Add New Review
        </Button>
      </div>

      {/* Reviews Grid */}
      <ResponsiveMasonry
        columnsCountBreakPoints={{ 350: 1, 750: 2, 900: 3, 1200: 4 }}
        className="mb-4"
      >
        <Masonry gutter="16px">
          {reviews.map((review) => (
            <Card
              key={review._id}
              className="mb-3 h-100 p-0"
              style={{ maxWidth: "auto" }}
            >
              <Card.Body className="d-flex flex-column h-100 p-3">
                {review.imageUrl && (
                  <Image
                    src={review.imageUrl}
                    alt={review.name}
                    fluid
                    className="mb-3 rounded"
                  />
                )}
                <Card.Title>{review.name}</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                  {review.address}
                </Card.Subtitle>
                <Card.Text className="flex-grow-1">{review.content}</Card.Text>
                <div className="d-flex justify-content-end mt-auto">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-2"
                    onClick={() => handleEdit(review._id)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(review._id)}
                  >
                    Delete
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ))}
        </Masonry>
      </ResponsiveMasonry>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode ? "Edit Review" : "Add New Review"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Client Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.get("name")?.toString() || ""}
                  onChange={handleInputChange}
                  placeholder="Enter client name"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  name="address"
                  value={formData.get("address")?.toString() || ""}
                  onChange={handleInputChange}
                  placeholder="Enter client address"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Review Content</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="content"
                  value={formData.get("content")?.toString() || ""}
                  onChange={handleInputChange}
                  placeholder="Enter review content"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Client Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Form.Group>

              {previewImage && (
                <div className="mt-3">
                  <Image
                    src={previewImage}
                    alt="Preview"
                    fluid
                    className="rounded"
                    style={{ maxHeight: "200px" }}
                  />
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {editMode ? "Update Review" : "Add Review"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ClientReviews;
